import React from 'react';
import * as Yup from 'yup';
import { withFormik, Field } from 'formik';
import { Form, Button } from 'semantic-ui-react';
import { withLocalize } from 'react-localize-redux';
import { connect } from 'react-redux';
import { compose, graphql } from 'react-apollo';
import { branch, renderComponent } from 'recompose';
import { map, upperFirst } from 'lodash';
import gql from 'graphql-tag';
import { push } from 'react-router-redux';
import store from '../../store';

import { MUTATE_POSTING, POSTING_FORM_DATA_QUERY } from '../postings/graphql';
import { HHInput, HHSingleSelect, HHTinyMCE } from './fields';
import { FORM_NAME } from '../forms/formConstant';
import Loader from '../common/loader';
import NotFound from '../common/notFound';
import { EXPERIENCE_LEVEL, CAD_SALARY_OPTIONS } from '../postings/constant';

const formikEnhancer = withFormik({
  validationSchema: ({ translate }) => Yup.object().shape({
    companyId: Yup.string().required(translate('form.error.mixed.required')),
    categoryId: Yup.string().required(translate('form.error.mixed.required')),
    title: Yup.string()
      .max(64, translate('form.error.string.max'))
      .required(translate('form.error.mixed.required')),
    email: Yup.string()
      .email(translate('form.error.string.email'))
      .required(translate('form.error.mixed.required')),
    location: Yup.string()
      .max(128, translate('form.error.string.max'))
      .required(translate('form.error.mixed.required')),
    experienceLevel: Yup.string().required(translate('form.error.mixed.required')),
    requirements: Yup.string(),
    responsibilities: Yup.string(),
    salary: Yup.string().required(translate('form.error.mixed.required'))
  }),
  mapPropsToValues: props => props.initialValues,
  handleSubmit: async (values, { props }) => {
    const { mutatePosting, postingId } = props;
    const { token } = store.getState().user;
    const variables = { ...values, token };

    [variables.yearlySalaryLow, variables.yearlySalaryHigh] = values.salary.split(', ');

    // append postingId for updating posting
    if (postingId) variables.postingId = postingId;

    await mutatePosting({ variables });

    props.push('/posting/manage');
  },
  displayName: FORM_NAME.CREATE_POSTING_FORM
});

const PostingForm = ({
  companyOptions,
  categoryOptions,
  handleSubmit,
  isSubmitting,
  isEditing,
  user: { token },
  createCompany,
  translate
}) => (
  <Form onSubmit={handleSubmit} className='posting-form-create'>
    <Form.Group widths='equal'>
      <Form.Field>
        <label htmlFor='companyId'>{translate('company')}</label>
        <Field
          name='companyId'
          component={HHSingleSelect}
          otherProps={{
            id: 'companyId',
            creatable: true,
            searchable: true,
            clearable: false,
            simpleValue: true,
            options: companyOptions,
            placeholder: translate('select company'),
            onNewOptionClick: option => {
              createCompany({
                variables: { token, name: option.value },
                refetchQueries: [{ query: POSTING_FORM_DATA_QUERY }]
              });
            }
          }}
        />
      </Form.Field>
      <Form.Field>
        <label htmlFor='categoryId'>{translate('category')}</label>
        <Field
          name='categoryId'
          component={HHSingleSelect}
          otherProps={{
            id: 'categoryId',
            creatable: false,
            searchable: true,
            clearable: false,
            simpleValue: true,
            options: categoryOptions,
            placeholder: translate('select category')
          }}
        />
      </Form.Field>
    </Form.Group>
    <Form.Group widths='equal'>
      <Form.Field>
        <label htmlFor='title'>{translate('title')}</label>
        <Field
          name='title'
          component={HHInput}
          otherProps={{
            id: 'title',
            type: 'text',
            maxLength: 64
          }}
        />
      </Form.Field>
      <Form.Field>
        <label htmlFor='email'>{translate('email')}</label>
        <Field
          name='email'
          component={HHInput}
          otherProps={{ id: 'email', type: 'email' }}
        />
      </Form.Field>
    </Form.Group>

    <Form.Group widths='equal'>
      <Form.Field>
        <label htmlFor='location'>{translate('location')}</label>
        <Field
          name='location'
          component={HHInput}
          otherProps={{
            id: 'location',
            type: 'text',
            maxLength: 128
          }}
        />
      </Form.Field>
      <Form.Field>
        <label htmlFor='experienceLevel'>{translate('experience level')}</label>
        <Field
          name='experienceLevel'
          component={HHSingleSelect}
          otherProps={{
            id: 'experienceLevel',
            clearable: true,
            searchable: true,
            simpleValue: true,
            options: EXPERIENCE_LEVEL,
            placeholder: translate('select exp lvl')
          }}
        />
      </Form.Field>
    </Form.Group>

    <Form.Field>
      <label htmlFor='salary'>{translate('salary')}</label>
      <Field
        name='salary'
        component={HHSingleSelect}
        otherProps={{
          id: 'salary',
          creatable: false,
          searchable: true,
          clearable: false,
          simpleValue: true,
          options: CAD_SALARY_OPTIONS,
          placeholder: translate('select category')
        }}
      />
    </Form.Field>

    <Form.Field>
      <label>{translate('requirements')}</label>
      <Field name='requirements' component={HHTinyMCE} />
    </Form.Field>

    <Form.Field>
      <label>{translate('responsibilities')}</label>
      <Field name='responsibilities' component={HHTinyMCE} />
    </Form.Field>

    <Form.Field>
      <Button
        fluid
        primary
        disabled={isSubmitting}
        loading={isSubmitting}
        className='g-rounded g-gradient'
        type='submit'
        size='big'
      >
        {translate(isEditing ? 'edit job posting' : 'create job posting')}
      </Button>
    </Form.Field>
  </Form>
);

const createCompanyMutation = gql`
  mutation($token: String, $name: String) {
    createCompany(companyInput: { token: $token, name: $name }) {
      code
      status
      company {
        id
        name
      }
    }
  }
`;

export default compose(
  withLocalize,
  graphql(POSTING_FORM_DATA_QUERY),
  branch(props => props.data.loading, renderComponent(() => <Loader />)),
  branch(props => props.data.error, renderComponent(() => <NotFound />)),
  connect(({ user }, { data: { allCategories, allCompanies } }) => ({
    user,
    categoryOptions: map(allCategories, cat => ({
      label: upperFirst(cat.name),
      value: cat.id
    })),
    companyOptions: map(allCompanies, comp => ({
      label: comp.name,
      value: comp.id
    }))
  })),
  graphql(createCompanyMutation, { name: 'createCompany' }),
  graphql(MUTATE_POSTING, { name: 'mutatePosting' }),
  connect(null, { push }),
  formikEnhancer
)(PostingForm);



// WEBPACK FOOTER //
// ./src/components/forms/jobPostingForm.js